import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from '../../auth/auth.service';
import { passwordValidator } from '../../components/input-password/password.validator';
import { confirmPasswordValidator } from '../../form/confirm-password.validator';
import { FormHelper } from '../../form/form-helper';
import { FormHelperFactory } from '../../form/form-helper.factory';
import { routes } from '../../routes/routes';
import { SnackbarService } from '../../service/snackbar.service';

export interface PasswordResetForm {
  password: FormControl<string>;
  confirmPassword: FormControl<string>;
}

@Component({
  selector: 'app-password-reset-page',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
})
export class PasswordResetPageComponent implements OnInit {
  passwordForm: FormGroup<PasswordResetForm>;
  formHelper: FormHelper<PasswordResetForm>;
  isResetting = false;

  protected readonly routes = routes;

  private oobCode = '';

  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private snackBar: SnackbarService,
    private translateService: TranslateService,
    formHelperFactory: FormHelperFactory,
  ) {
    const fb = new FormBuilder();
    this.passwordForm = fb.group(
      {
        password: fb.nonNullable.control('', [Validators.required, passwordValidator()]),
        confirmPassword: fb.nonNullable.control('', [Validators.required]),
      },
      {
        updateOn: 'submit',
        validators: [confirmPasswordValidator()],
      },
    );
    this.formHelper = formHelperFactory.create(this.passwordForm);
  }

  ngOnInit() {
    // Component initialization logic can be added here if needed
    this.oobCode = this.activatedRoute.snapshot.queryParamMap.get('oobCode') ?? '';
  }

  async resetPassword() {
    this.formHelper.trackSubmitted();
    if (!this.passwordForm.valid) {
      return;
    }

    try {
      this.isResetting = true;
      await this.authService.confirmPasswordReset(this.oobCode, this.passwordForm.controls.password.value);
      this.snackBar.showSuccess(this.translateService.instant('PAGE.PASSWORD_RESET.NOTIFICATION.SUCCESS'));
      // Redirect to the user's dashboard after successful password reset
      const currentUser = await this.authService.getCurrentUser();
      if (currentUser) {
        this.router.navigateByUrl(currentUser.redirectUrlAfterLogin);
      } else {
        this.router.navigateByUrl(routes.login.url());
      }
    } catch (e) {
      console.log(e);

      this.snackBar.showError(this.translateService.instant('PAGE.PASSWORD_RESET.NOTIFICATION.ERROR'));
    } finally {
      this.isResetting = false;
    }
  }
}
